"use client";

import React, { useState } from "react";
import { Database, HardDrive, MessageSquare, ShieldCheck, Zap, Layers3 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
type ResourceType = "vectors" | "storage" | "queries";
interface PackData {
  id: string;
  name: string;
  description: string;
  price: string;
  resources: {
    vectors: string;
    storage: string;
    queries: string;
  };
  ctaText: string;
  highlight?: boolean;
}
const packs: PackData[] = [{
  id: "pack-s",
  name: "Pack S",
  description: "A smaller expansion for controlled growth in one resource category.",
  price: "$199",
  resources: {
    vectors: "+1M vectors",
    storage: "+10GB indexed storage",
    queries: "+50k queries / month"
  },
  ctaText: "Add Pack S"
}, {
  id: "pack-m",
  name: "Pack M",
  description: "A standard expansion for growing teams, agencies, and tenant environments.",
  price: "$499",
  resources: {
    vectors: "+3M vectors",
    storage: "+30GB indexed storage",
    queries: "+150k queries / month"
  },
  ctaText: "Add Pack M",
  highlight: true
}, {
  id: "pack-l",
  name: "Pack L",
  description: "A larger expansion for higher-volume usage and more demanding delivery models.",
  price: "$999",
  resources: {
    vectors: "+8M vectors",
    storage: "+80GB indexed storage",
    queries: "+400k queries / month"
  },
  ctaText: "Add Pack L"
}];
function CapacityCard({
  pack,
  index
}: {
  pack: PackData;
  index: number;
}) {
  const [selectedResource, setSelectedResource] = useState<ResourceType>("vectors");
  const handleValueChange = (value: string) => {
    if (value) setSelectedResource(value as ResourceType);
  };
  return <Card data-index={index} className={`flex h-full w-full flex-col rounded-2xl border transition-all duration-200 ${pack.highlight ? "border-primary ring-2 ring-primary/15 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.14)]" : "border-border/70 shadow-[0_10px_30px_-18px_rgba(0,0,0,0.12)]"}`}>
      <div className="flex flex-1 flex-col p-6">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-2xl font-semibold text-foreground">{pack.name}</h3>
          <div className="rounded-lg bg-primary/10 p-2.5">
            <Zap className="h-5 w-5 text-primary" />
          </div>
        </div>

        <p className="min-h-[48px] text-sm leading-relaxed text-muted-foreground">
          {pack.description}
        </p>

        <div className="mt-5 space-y-3">
          <span className="text-sm font-semibold text-foreground">
            Choose one resource to expand
          </span>

          <ToggleGroup type="single" value={selectedResource} onValueChange={handleValueChange} variant="outline" className="w-full justify-start rounded-lg bg-secondary/40 p-1">
            <ToggleGroupItem value="vectors" className="flex-1 text-xs data-[state=on]:bg-background data-[state=on]:shadow-xs sm:text-sm">
              <Database className="mr-2 hidden h-4 w-4 sm:inline-block" />
              Vectors
            </ToggleGroupItem>
            <ToggleGroupItem value="storage" className="flex-1 text-xs data-[state=on]:bg-background data-[state=on]:shadow-xs sm:text-sm">
              <HardDrive className="mr-2 hidden h-4 w-4 sm:inline-block" />
              Storage
            </ToggleGroupItem>
            <ToggleGroupItem value="queries" className="flex-1 text-xs data-[state=on]:bg-background data-[state=on]:shadow-xs sm:text-sm">
              <MessageSquare className="mr-2 hidden h-4 w-4 sm:inline-block" />
              Queries
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="mt-5 flex min-h-[118px] flex-col items-center justify-center rounded-xl border border-accent bg-accent/30 p-6 text-center">
          <span className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            This pack adds
          </span>
          <span className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            {pack.resources[selectedResource]}
          </span>
        </div>

        <div className="mt-5 rounded-lg border border-border/60 bg-background p-4 text-sm leading-relaxed text-muted-foreground">
          Capacity Packs expand <strong className="text-foreground">one resource type only</strong>.
          They do not increase storage, vectors, and queries at the same time.
        </div>

        <div className="mt-5 flex items-end justify-between gap-4">
          <div className="flex flex-col">
            <span className="text-3xl font-bold text-foreground">{pack.price}</span>
            <span className="text-sm text-muted-foreground">/ month per pack</span>
          </div>

          <Button variant={pack.highlight ? "default" : "outline"} size="lg" className="h-11 px-5">
            {pack.ctaText}
          </Button>
        </div>

        <div className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-secondary/30 py-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" />
          <span>Hard caps remain enforced. No surprise overages.</span>
        </div>
      </div>
    </Card>;
}
export default function ScalableCapacityPacks() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="scalable-capacity-packs" className="relative overflow-hidden bg-background py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={cn("mx-auto mb-12 max-w-3xl text-center transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <Layers3 className="size-4" />
            Capacity Packs
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            Scale the resource that becomes your bottleneck
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
            Choose the pack size that fits your growth and apply it to the resource that needs more room,
            without changing your full plan structure.
          </p>
        </div>

        <div ref={gridRef} className={cn("grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3 transition-all duration-700 ease-out", isGridVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
          {packs.map((pack, i) => <CapacityCard key={pack.id} pack={pack} index={i} />)}
        </div>
      </div>
    </section>;
}